/**
 * Renewal status for a vault document, for the admin table.
 *
 * Deliberately mirrors `frontend/src/Pages/Documents/status.js`. The two apps do not
 * share a build, so this is a copy rather than an import — but the thresholds and the
 * date arithmetic MUST stay identical, otherwise an admin and the customer looking at
 * the same document see different urgency, and support conversations go wrong.
 *
 * Thresholds: red = overdue or < 30 days · amber = 30-60 inclusive · green = > 60
 * · grey = no or unparseable date.
 *
 * Both dates are normalised to UTC midnight before subtracting. Local-midnight
 * arithmetic drifts by an hour across a DST transition, which is enough to shift a
 * boundary day by one — a bug that already bit this feature once on the customer side.
 */
export const getDocumentStatus = (renewalDate, referenceDate = new Date()) => {
  const grey = { status: "grey", daysRemaining: null, label: "No renewal date" };

  if (!renewalDate) return grey;

  const renewal = new Date(renewalDate);
  if (Number.isNaN(renewal.getTime())) return grey;

  const midnightUTC = (d) =>
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());

  const daysRemaining = Math.round(
    (midnightUTC(renewal) - midnightUTC(referenceDate)) / (1000 * 60 * 60 * 24)
  );

  const plural = `${daysRemaining} day${daysRemaining === 1 ? "" : "s"} left`;

  if (daysRemaining < 0) return { status: "red", daysRemaining, label: "Overdue" };
  if (daysRemaining < 30) {
    return {
      status: "red",
      daysRemaining,
      label: daysRemaining === 0 ? "Renews today" : plural,
    };
  }
  if (daysRemaining <= 60) return { status: "amber", daysRemaining, label: plural };
  return { status: "green", daysRemaining, label: plural };
};

export default getDocumentStatus;
